import React, { useState, useEffect } from 'react';
import emergencyService from '../services/emergencyService';
import locationService from '../services/locationService';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../hooks/useNotifications';
import { useCall } from '../hooks/useCall';

interface EmergencyButtonProps {
  className?: string;
}

const EmergencyButton: React.FC<EmergencyButtonProps> = ({ className }) => {
  const [isEmergency, setIsEmergency] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { user, token } = useAuth();
  const { notifications } = useNotifications();
  const { endCall } = useCall();

  useEffect(() => {
    checkEmergencyStatus();
  }, []);

  const checkEmergencyStatus = async () => {
    try {
      const isActive = await emergencyService.isEmergencyActive();
      setIsEmergency(isActive);
    } catch (error) {
      console.error('Error checking emergency status:', error);
    }
  };

  const handleEmergency = async () => {
    if (!token) {
      console.error('No authentication token found');
      return;
    }

    setIsLoading(true);
    try {
      // Get current location
      const location = await locationService.getCurrentLocation();

      // Get emergency contacts
      const contacts = await emergencyService.getEmergencyContacts();

      // Trigger emergency with high priority
      await emergencyService.triggerEmergency(location, true);
      setIsEmergency(true);

      // End any ongoing calls
      endCall();

      // Broadcast emergency to all contacts
      await emergencyService.broadcastEmergency(contacts);
    } catch (error) {
      console.error('Error triggering emergency:', error);
      setIsEmergency(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResolveEmergency = async () => {
    if (!token) {
      console.error('No authentication token found');
      return;
    }

    setIsLoading(true);
    try {
      await emergencyService.resolveEmergency();
      setIsEmergency(false);
    } catch (error) {
      console.error('Error resolving emergency:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return null; // Don't show the button if not authenticated
  }

  return (
    <>
      <button
        className={`emergency-button ${isEmergency ? 'active' : ''} ${className || ''}`}
        onClick={isEmergency ? handleResolveEmergency : handleEmergency}
        disabled={isLoading}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: isEmergency ? '#4CAF50' : '#ff0000',
          border: 'none',
          color: 'white',
          fontSize: '24px',
          cursor: 'pointer',
          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
          transition: 'all 0.3s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: isEmergency ? 'pulse 1s infinite' : 'none'
        }}
      >
        {isEmergency ? '✓' : '!'}
      </button>

      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.1); }
            100% { transform: scale(1); }
          }

          .emergency-button:hover {
            transform: scale(1.1);
          }

          .emergency-button:active {
            transform: scale(0.95);
          }

          .emergency-button.active {
            animation: pulse 1s infinite;
          }
        `}
      </style>
    </>
  );
};

export default EmergencyButton; 